import { useEffect, useMemo, useState } from "react";
import { apiGet } from "@/lib/apiClient";

export type CrmOrgUserRow = {
  id: string;
  email?: string | null;
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  role?: string;
  isActive?: boolean;
  jobTitle?: string | null;
};

type OrgResponse = { domain?: string | null };

type UsersResponse = {
  users?: CrmOrgUserRow[];
};

export function crmOrgUserDisplayName(user: CrmOrgUserRow): string {
  return (
    user.name?.trim() ||
    [user.firstName, user.lastName].filter(Boolean).join(" ").trim() ||
    user.email?.trim() ||
    user.id
  );
}

export function buildCrmOrgUserLookup(users: CrmOrgUserRow[]): Record<string, CrmOrgUserRow> {
  const byKey: Record<string, CrmOrgUserRow> = {};
  for (const user of users) {
    const id = user.id?.trim();
    if (id) byKey[id.toLowerCase()] = user;
    const email = user.email?.trim();
    if (email) byKey[email.toLowerCase()] = user;
  }
  return byKey;
}

export function resolveCrmOwnerLabel(
  ownerId: string | null | undefined,
  lookup: Record<string, CrmOrgUserRow>,
): string {
  const raw = ownerId?.trim();
  if (!raw) return "Unassigned";
  const match = lookup[raw.toLowerCase()];
  if (match) return crmOrgUserDisplayName(match);
  return raw;
}

export async function fetchCrmOrgUserDirectory(): Promise<{
  users: CrmOrgUserRow[];
  lookup: Record<string, CrmOrgUserRow>;
}> {
  const orgRes = await apiGet<OrgResponse>("/api/me/organization");
  const domain = orgRes.data?.domain?.trim();
  if (!orgRes.ok || !domain) {
    return { users: [], lookup: {} };
  }
  const usersRes = await apiGet<UsersResponse>(
    `/api/users?accountDomain=${encodeURIComponent(domain)}&limit=200`,
  );
  const users = usersRes.ok ? (usersRes.data?.users ?? []) : [];
  return { users, lookup: buildCrmOrgUserLookup(users) };
}

export function useCrmOrgUserDirectory() {
  const [users, setUsers] = useState<CrmOrgUserRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const result = await fetchCrmOrgUserDirectory();
      if (cancelled) return;
      setUsers(result.users);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const lookup = useMemo(() => buildCrmOrgUserLookup(users), [users]);

  const resolveOwnerLabel = (ownerId: string | null | undefined) =>
    resolveCrmOwnerLabel(ownerId, lookup);

  return { users, lookup, loading, resolveOwnerLabel };
}

export function sessionUserOwnerId(user: { id?: string; email?: string | null } | undefined): string {
  return user?.id?.trim() || user?.email?.trim() || "";
}
